import { type ReactNode } from 'react';

import { type BaseCustomField } from '@air/api/types';
import {
  BoardIcon,
  CalendarIcon,
  CameraIcon,
  CogIcon,
  CopyrightIcon,
  FilmIcon,
  FolderIcon,
  FolderOpenIcon,
  FormIcon,
  ImportIcon,
  KeyRoundIcon,
  MapPinIcon,
  MinusIcon,
  PaletteIcon,
  ScanSearchIcon,
  SparklesIcon,
  TagIcon,
  UserIcon,
  UsersIcon,
  VideoIcon,
} from '@air/design-icons';
import { type DropdownMenuOption } from '@air/primitive-dropdown-menu';

import { type SelectedFilter } from '~/store/filters/types';

export const AllDateFilters = ['dateCreated', 'dateUploaded', 'dateModified', 'dateTaken'] as const;
export type DateFilters = (typeof AllDateFilters)[number];

export const AllAudioVisualFilters = ['videoFrameRate', 'videoAspectRatio', 'audioCoding', 'audioSampleRate'] as const;
export type AudioVisualFilters = (typeof AllAudioVisualFilters)[number];

export const AllLocationFilters = ['country', 'city', 'state'] as const;
export type LocationFilters = (typeof AllLocationFilters)[number];

export const AllAdvancedFilters = ['camera', 'creator', 'copyright', 'importedKeywords'] as const;
export type AdvancedFilters = (typeof AllAdvancedFilters)[number];

export type FilterType =
  | AdvancedFilters
  | AudioVisualFilters
  | DateFilters
  | LocationFilters
  | 'audioVisual'
  | 'orientation'
  | 'color'
  | 'colorThreshold'
  | 'customField'
  | 'date'
  | 'location'
  | 'extension'
  | 'advanced'
  | 'other'
  | 'people'
  | 'smartTags'
  | 'source'
  | 'tags'
  | 'type'
  | 'assetFileKind'
  | 'uploader'
  | 'locations'
  | 'separator1'
  | 'separator2'
  | 'similarToImage'
  | 'similarTo';

const filtersInOrder: FilterType[] = [
  'locations',
  'type',
  'assetFileKind',
  'tags',
  'smartTags',
  'separator1',
  'customField',
  'uploader',
  'source',
  'color',
  'people',
  'camera',
  'creator',
  'copyright',
  'importedKeywords',
  'extension',
  'location',
  'city',
  'state',
  'country',
  'orientation',
  'audioVisual',
  'audioCoding',
  'audioSampleRate',
  'videoAspectRatio',
  'videoFrameRate',
  'date',
  'dateCreated',
  'dateModified',
  'dateTaken',
  'dateUploaded',
  'separator2',
  'other',
];

export const IPTC_FIELDS: FilterType[] = [
  'dateTaken',
  'camera',
  'importedKeywords',
  'copyright',
  'creator',
  'audioVisual',
  'videoFrameRate',
  'videoAspectRatio',
  'audioCoding',
  'audioSampleRate',
  'location',
  'city',
  'state',
  'country',
];

export const filtersOrder = filtersInOrder.reduce<Partial<Record<FilterType, number>>>((acc, filter, index) => {
  acc[filter] = index;
  return acc;
}, {});

export const sortFiltersByOrder = (filterA: FilterType, filterB: FilterType) =>
  (filtersOrder[filterA] ?? 0) - (filtersOrder[filterB] ?? 0);

export type FilterIcon = typeof MapPinIcon;

export type FilterBaseMenuOption = {
  id: FilterType;
  subOptions?: (DropdownMenuOption & { id: FilterType | string; 'data-testid'?: string; onClick?: () => void })[];
  label: string | ReactNode;
  icon?: FilterIcon;
};

export type CustomFieldFilterType = Pick<BaseCustomField, 'id' | 'name' | 'type'>;

export const baseFiltersOptions: Record<FilterType, FilterBaseMenuOption> = {
  orientation: { id: 'orientation', label: 'Orientation', icon: VideoIcon },
  audioCoding: { id: 'audioCoding', label: 'Audio Coding', icon: VideoIcon },
  audioSampleRate: { id: 'audioSampleRate', label: 'Audio Sample Rate', icon: VideoIcon },
  audioVisual: { id: 'audioVisual', label: 'Audio/Visual', icon: FilmIcon },
  camera: { id: 'camera', label: 'Camera', icon: CameraIcon },
  city: { id: 'city', label: 'City', icon: MapPinIcon },
  color: { id: 'color', label: 'Color', icon: PaletteIcon },
  copyright: { id: 'copyright', label: 'Copyright', icon: CopyrightIcon },
  country: { id: 'country', label: 'Country', icon: MapPinIcon },
  creator: { id: 'creator', label: 'Creator', icon: UserIcon },
  customField: { id: 'customField', label: 'Custom Fields', icon: FormIcon },
  date: { id: 'date', label: 'Date', icon: CalendarIcon },
  dateCreated: { id: 'dateCreated', label: 'Date Created', icon: CalendarIcon },
  dateModified: { id: 'dateModified', label: 'Date Modified', icon: CalendarIcon },
  dateTaken: { id: 'dateTaken', label: 'Date Taken', icon: CalendarIcon },
  dateUploaded: { id: 'dateUploaded', label: 'Date Uploaded', icon: CalendarIcon },
  extension: { id: 'extension', label: 'File Extension', icon: FolderIcon },
  importedKeywords: { id: 'importedKeywords', label: 'Imported Keywords', icon: KeyRoundIcon },
  location: { id: 'location', label: 'Embedded Location', icon: MapPinIcon },
  other: { id: 'other', label: 'Advanced', icon: CogIcon },
  people: {
    id: 'people',
    label: <div className="flex items-center gap-2">People</div>,
    icon: UsersIcon,
  },
  smartTags: { id: 'smartTags', label: 'Smart Tags', icon: SparklesIcon },
  source: { id: 'source', label: 'Upload Source', icon: ImportIcon },
  state: { id: 'state', label: 'State/Province', icon: MapPinIcon },
  tags: { id: 'tags', label: 'Tags', icon: TagIcon },
  type: { id: 'type', label: 'Type', icon: FolderIcon },
  uploader: { id: 'uploader', label: 'Uploaded By', icon: UserIcon },
  videoAspectRatio: { id: 'videoAspectRatio', label: 'Signal Aspect Ratio', icon: VideoIcon },
  videoFrameRate: { id: 'videoFrameRate', label: 'Video Frame Rate', icon: VideoIcon },
  locations: { id: 'locations', label: 'Locations', icon: BoardIcon },
  advanced: { id: 'advanced', label: 'EXIF & IPTC', icon: CameraIcon },
  separator1: { id: 'separator1', label: '', icon: MinusIcon },
  separator2: { id: 'separator2', label: '', icon: MinusIcon },
  assetFileKind: { id: 'assetFileKind', label: 'Asset File Kind', icon: FolderOpenIcon },
  colorThreshold: { id: 'colorThreshold', label: 'Color Threshold', icon: PaletteIcon },
  similarToImage: { id: 'similarToImage', label: 'Similar To Image', icon: ScanSearchIcon },
  similarTo: { id: 'similarTo', label: 'Similar To', icon: ScanSearchIcon },
};

export type FilterSelectType = (filter: FilterType, customField?: CustomFieldFilterType) => void;

export type GetFiltersMenuOptionsParams = {
  onSelect: FilterSelectType;
  customFields: CustomFieldFilterType[];
  selectedFilters?: SelectedFilter[];
  availableFilters: FilterType[];
  disabledFilters?: FilterType[];
};
